import { createReducer } from 'reduxsauce';
import { Types as ActionTypes } from './actions';

export const INITIAL_STATE = {
    // Config
    config: {
        authorizationApiUrl: '',
        lang: '',
        location: '',
        organizationId: 0,
        organizationUnitId: 0,
        pictureBaseUrl: '',
        quoteApiUrl: '',
        showroomApiUrl: '',
        token: '',
        vehicleApiUrl: ''
    },

    accountId: 0,
    leadId: 0,

    // Handling saga error, for now, no way to come back from a global error
    error: null,

    quote: {},

    hasTaxesIncluded: false,

    // PaymentBar, PaymentCard
    meta: {},
    paymentOptions: {
        cashDown: 0,
        cashPurchaseOption: {},
        financeOptions: [],
        financePlan: '',
        disclaimer: '',
        kmPerYearPlan: 0,
        lien: 0,
        odometer: 0,
        paymentFrequency: 0,
        purchaseMethod: '',
        saleType: '',
        taxPlan: '',
        term: {},
        tradeIn: 0,
        transactionToken: '',
        vehicleCondition: ''
    },

    // Toggle Modal View
    modalShow: false,
    optionDetails: [],

    // check if is readOnly mode
    isReadOnly: false,

    selectedTab: 'cash',
};

const handleError = (state, payload) => {
    const { error } = payload;
    const { name, message } = error;

    return {
        ...state,
        error: { name, message }
    };
};

const initializeStore = (state = INITIAL_STATE, payload) => {
    const {
        token,
        lang,
        queryParams,
        serverRuntimeConfig
    } = payload;
    const {
        location,
        organizationId,
        organizationUnitId
    } = queryParams;
    const {
        authorizationApiUrl,
        pictureBaseUrl,
        quoteApiUrl,
        showroomApiUrl,
        vehicleApiUrl
    } = serverRuntimeConfig;

    return {
        ...state,
        config: {
            authorizationApiUrl,
            lang,
            location: location || 'qc',
            organizationId,
            organizationUnitId,
            pictureBaseUrl,
            quoteApiUrl,
            showroomApiUrl,
            token: token.replace('Bearer ', ''),
            vehicleApiUrl
        }
    };
};

export const loadQuoteSuccess = (state = INITIAL_STATE, payload) => {
    const { quote } = payload;
    const {
        accountId,
        leadId,
        province,
        purchaseMethod,
        vehicle,
        odometer,
        __paymentOptionsMeta: meta
    } = quote;

    const purchaseMethodDetails = (purchaseMethod === "finance" || purchaseMethod === "lease") ? quote.finance : quote.cashPurchase;
    const { disclaimer, tradeIn, cashDown = 0, paymentFrequency = 0, lien = 0, kmPerYearPlan = 0, term, tradeInTaxCreditsDetails } = purchaseMethodDetails
    const { transactionToken, vehicleCondition } = vehicle

    const paymentOptions = {
        ...state.paymentOptions,
        cashDown,
        kmPerYearPlan,
        lien,
        odometer,
        paymentFrequency,
        purchaseMethod,
        disclaimer,
        tradeIn,
        tradeInTaxCreditsDetails,
        transactionToken,
        vehicleCondition,
        term,
        saleType: "retail",
        taxPlan: "standard"
    }

    if (purchaseMethod !== 'cash') {
        paymentOptions.term = term.term
    }

    const financePlans = meta.financePlans.map(plan => ({
        ...plan,
        term: plan.term.sort().reverse()
    }));

    return {
        ...state,
        accountId,
        leadId,
        province,
        isQuoteChanged: false,
        quote: {
            ...state.quote,
            ...quote
        },
        meta: {
            ...state.meta,
            ...meta,
            financePlans
        },
        paymentOptions
    };
};

/**
 * @param {Object} state
 * @param {Object} payload - type, response
 * @returns {Object} new state
 */
export const updatePaymentOptionsSuccess = (state, payload) => {
    const { response } = payload;

    const {
        cashDown,
        cashPurchase,
        cashPurchaseOption,
        disclaimer,
        finance,
        financeOptions,
        financePlan,
        kmPerYearPlan,
        lien,
        odometer,
        paymentFrequency,
        purchaseMethod,
        saleType,
        taxPlan,
        term,
        tradeIn,
        transactionToken,
        vehicleCondition
    } = response;

    // const purchaseMethodDetails = (purchaseMethod === "finance" || purchaseMethod === "lease") ? quote[purchaseMethod] : quote.cashPurchase;

    return {
        ...state,
        quote: {
            ...state.quote,
            cashPurchase,
            finance
        },
        paymentOptions: {
            ...state.paymentOptions,
            cashDown,
            cashPurchaseOption,
            financeOptions,
            financePlan,
            disclaimer,
            kmPerYearPlan,
            lien,
            odometer,
            paymentFrequency,
            purchaseMethod,
            saleType,
            taxPlan,
            term,
            tradeIn,
            transactionToken,
            vehicleCondition
        }
    };
};

/**
 * @param {Object} state
 * @param {Object} payload - type, response
 * @returns {Object} Return updated state
 */
export const updateTransactionDetailsSuccess = (state, payload) => {
    const { transaction } = payload.response;
    return {
        ...state,
        quote: {
            ...state.quote,
            transaction
        }
    };
};

/**
 * @param {Object} state - global state
 * @param {Object} payload - type, hasTaxesIncluded
 * @returns {Object} new state
 */
const updateTaxesIncluded = (state, payload) => {
    const { hasTaxesIncluded } = payload;

    return {
        ...state,
        hasTaxesIncluded
    };
};

/**
 * @param {Object} state - global state
 * @param {Object} payload - type, hasTaxesIncluded
 * @returns {Object} new state
 */
const updateItemValidation = (state, payload) => {
    const { itemValidation } = payload;

    return {
        ...state,
        itemValidation
    };
};


const updateTransactionOptionsSuccess = (state, payload) => {
    return {
        ...state,
        optionDetails: payload.options
    };
};

const resetItemValidation = (state) => {
    const { itemValidation } = INITIAL_STATE;

    return {
        ...state,
        itemValidation
    };
};

const setReadOnly = (state, { isReadOnly }) => ({
    ...state,
    isReadOnly
});

const changeViewingPurchaseOption = (state, { value }) => ({
    ...state,
    defaultState: {
        ...state.defaultState,
        viewingPurchaseOption: value
    }
})

const updateSelectedTab = (state, payload) => {
    return {
        ...state,
        selectedTab: payload.tab
    }
};

export default createReducer(INITIAL_STATE, {
    [ActionTypes.HANDLE_ERROR]: handleError,
    [ActionTypes.INITIALIZE_STORE]: initializeStore,
    [ActionTypes.LOAD_QUOTE_SUCCESS]: loadQuoteSuccess,
    [ActionTypes.RESET_ITEM_VALIDATION]: resetItemValidation,
    [ActionTypes.RESET_ITEM_VALIDATION]: resetItemValidation,
    [ActionTypes.SET_READ_ONLY]: setReadOnly,
    [ActionTypes.UPDATE_ITEM_VALIDATION]: updateItemValidation,
    [ActionTypes.UPDATE_PAYMENT_OPTIONS_SUCCESS]: updatePaymentOptionsSuccess,
    [ActionTypes.UPDATE_TAXES_INCLUDED]: updateTaxesIncluded,
    [ActionTypes.UPDATE_SELECTED_TAB]: updateSelectedTab,
});
